import { useState, useEffect } from 'react';
import { createConsumable, updateConsumable, lookupUpc, getConsumables } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import BarcodeScanner from './BarcodeScanner';

const CATEGORIES = ['filter', 'bulb', 'battery', 'cleaning', 'plumbing', 'hvac', 'other'];

const blank = {
  name: '', brand: '', partNumber: '', upc: '', category: 'other',
  linkedTask: '',
  quantityOnHand: 0, reorderAt: 1, reorderQty: 1,
  unitCost: '', storeName: '', storeUrl: '', notes: '',
};

const ConsumableForm = ({ existing, onClose, onSaved }) => {
  const { tasks } = useApp();
  const [form, setForm] = useState(existing ? {
    ...blank,
    ...existing,
    unitCost: existing.unitCost ?? '',
    linkedTask: existing.linkedTask ?? '',
  } : blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState('');
  const [dupNotice, setDupNotice] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.upc === '') delete payload.upc;
      if (payload.unitCost === '') delete payload.unitCost;
      else payload.unitCost = parseFloat(payload.unitCost);
      payload.quantityOnHand = parseFloat(payload.quantityOnHand) || 0;
      payload.reorderAt = parseFloat(payload.reorderAt) || 0;
      payload.reorderQty = parseFloat(payload.reorderQty) || 0;

      const { data } = existing
        ? await updateConsumable(existing._id, payload)
        : await createConsumable(payload);
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDetected = async (upc) => {
    setScanning(false);
    setScanError('');
    setDupNotice(null);
    setScanLoading(true);
    // Retain the barcode even if the lookup fails, so a future scan of the
    // same item can match this record
    setForm((prev) => ({ ...prev, upc }));
    try {
      const [{ data }, dupes] = await Promise.all([
        lookupUpc(upc),
        getConsumables({ upc }).then((r) => r.data).catch(() => []),
      ]);
      const existingMatch = dupes.find((d) => d._id !== existing?._id);
      if (existingMatch) {
        setDupNotice(`Already in your supplies: ${existingMatch.name} — ${existingMatch.quantityOnHand} on hand. Saving will create a duplicate; consider restocking instead.`);
      }
      if (data.found) {
        setForm((prev) => ({
          ...prev,
          name: data.name || prev.name,
          brand: data.brand || prev.brand,
          category: data.category || prev.category,
        }));
      } else {
        setScanError('Product not found in database. Please enter details manually.');
      }
    } catch {
      setScanError('Lookup failed. Please enter details manually.');
    } finally {
      setScanLoading(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{existing ? 'Edit Item' : 'Add Supply Item'}</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          <form onSubmit={handleSubmit} className="modal-form">
            {!existing && (
              <div style={{ marginBottom: '0.75rem' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setScanError(''); setScanning(true); }} disabled={scanLoading}>
                  {scanLoading ? 'Looking up…' : '📷 Scan Barcode'}
                </button>
                {form.upc && <span style={{ marginLeft: '0.6rem', fontSize: '0.8rem', color: 'var(--text-muted, #6b7280)' }}>UPC: {form.upc}</span>}
                {scanError && <p className="form-error" style={{ marginTop: '0.4rem' }}>{scanError}</p>}
                {dupNotice && <p style={{ marginTop: '0.4rem', fontSize: '0.85rem', color: '#b45309' }}>⚠ {dupNotice}</p>}
              </div>
            )}
          <div className="form-row">
            <label>Item Name *
              <input name="name" value={form.name} onChange={set} required />
            </label>
            <label>Category
              <select name="category" value={form.category} onChange={set}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label>Brand
              <input name="brand" value={form.brand} onChange={set} />
            </label>
            <label>Part / Model #
              <input name="partNumber" value={form.partNumber} onChange={set} />
            </label>
          </div>
          <div className="form-row">
            <label>Qty on Hand
              <input type="number" name="quantityOnHand" value={form.quantityOnHand} onChange={set} min={0} step="any" />
            </label>
            <label>Reorder When ≤
              <input type="number" name="reorderAt" value={form.reorderAt} onChange={set} min={0} step="any" />
            </label>
            <label>Reorder Qty
              <input type="number" name="reorderQty" value={form.reorderQty} onChange={set} min={0} step="any" />
            </label>
          </div>
          <div className="form-row">
            <label>Unit Cost ($)
              <input type="number" name="unitCost" value={form.unitCost} onChange={set} min={0} step="0.01" placeholder="0.00" />
            </label>
            <label>Store
              <input name="storeName" value={form.storeName} onChange={set} placeholder="Home Depot" />
            </label>
          </div>
          <label>Store URL
            <input name="storeUrl" value={form.storeUrl} onChange={set} placeholder="https://…" />
          </label>
          <label>Linked Task (auto-decrement when task is completed)
            <select name="linkedTask" value={form.linkedTask} onChange={set}>
              <option value="">— none —</option>
              {tasks.map((t) => (
                <option key={t._id} value={t._id}>{t.title}</option>
              ))}
            </select>
          </label>
          <label>Notes
            <textarea name="notes" value={form.notes} onChange={set} rows={2} />
          </label>

            {error && <p className="form-error">{error}</p>}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : existing ? 'Save Changes' : 'Add Item'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
      {scanning && <BarcodeScanner onDetected={handleDetected} onClose={() => setScanning(false)} />}
    </>
  );
};

export default ConsumableForm;
